import axios, { AxiosError } from 'axios'
import { toast } from 'react-toastify'
import { axiosPrivate } from './api'
import { isValidAccessToken } from './auth'
import { StoreType } from '../store'
import { clear, logout } from '../features/authSlice'
import { CURRENT_USER_URL } from '../api/profile'

const FALLBACK_403_MESSAGE = 'No tienes permisos para realizar esta acción.'

// El backend responde 403 a un token con firma inválida/revocada aunque `exp` siga vigente, pero 401/403
// también significan otras cosas (sin permisos). Por eso, ante cualquiera de los dos se pregunta a users/current
// si la sesión sigue viva y solo se cierra si también la rechaza.
// Se verifica contra CORE porque emite los tokens: si lo acepta, un nuevo login no arreglaría el rechazo de otra API.
const SESSION_ERROR_STATUSES = [401, 403]
// Sin timeout, una verificación colgada dejaría esperando a todos los errores siguientes del mismo token
const SESSION_CHECK_TIMEOUT = 10_000
let sessionCheck: { token: string; promise: Promise<boolean> } | null = null

const isSessionError = (error: AxiosError) => SESSION_ERROR_STATUSES.includes(error.response?.status ?? 0)

const getRequestToken = (error: AxiosError) => {
   const authorization = error.config?.headers?.['Authorization']
   return typeof authorization === 'string' ? authorization.replace('Bearer ', '') : ''
}

const showForbiddenToast = (error: AxiosError<{ error?: string }>) => {
   if (error.response?.status === 403 && error.config?.method === 'get') {
      const message: string = error.response?.data?.error || FALLBACK_403_MESSAGE
      toast.error(message)
   }
}

// Varios errores simultáneos del mismo token comparten la misma verificación.
// Usa axios directo (no axiosPrivate) para no volver a pasar por este interceptor.
const isSessionAlive = (token: string) => {
   if (!sessionCheck || sessionCheck.token !== token) {
      const check = {
         token,
         promise: axios
            .get(CURRENT_USER_URL, { headers: { Authorization: `Bearer ${token}` }, timeout: SESSION_CHECK_TIMEOUT })
            .then(() => true)
            .catch((error: AxiosError) => !isSessionError(error))
            .finally(() => {
               if (sessionCheck === check) {
                  sessionCheck = null
               }
            }),
      }
      sessionCheck = check
   }
   return sessionCheck.promise
}

export const setUpInterceptor = (store: StoreType) => {
   axiosPrivate.interceptors.request.use(
      async (config) => {
         const { authToken, base } = store.getState() || {}
         const access_token = authToken?.token
         if (base.account && config?.headers) {
            config.headers['account'] = base.account.code
         }
         if (base.locale && config?.headers) {
            config.headers['Accept-Language'] = base.locale
            if (config.method === 'get') {
               config.params = { locale: base.locale, ...config.params }
            } else {
               config.data = { locale: base.locale, ...config.data }
            }
         }
         if (!isValidAccessToken(access_token)) {
            store.dispatch(clear())
         } else {
            if (config?.headers) {
               config.headers['Authorization'] = `Bearer ${access_token}`
            }
         }

         return config
      },
      (error) => {
         return Promise.reject(error)
      },
   )

   axiosPrivate.interceptors.response.use(
      (response) => response,
      (error) => {
         const token = getRequestToken(error)
         if (!isSessionError(error) || !token) {
            showForbiddenToast(error)
            return Promise.reject(error)
         }

         // Una respuesta tardía de una sesión anterior (el usuario ya volvió a iniciar sesión) no debe tocar la actual
         const isCurrentSession = () => store.getState().authToken?.token === token
         if (isCurrentSession()) {
            isSessionAlive(token)
               .then((isAlive) => {
                  if (!isCurrentSession()) {
                     return
                  }
                  if (isAlive) {
                     showForbiddenToast(error)
                  } else {
                     store.dispatch(logout())
                  }
               })
               .catch((e) => console.error(e))
         }
         return Promise.reject(error)
      },
   )
}
